import { isBuyerOnlyProfile, normalizeAccessToken } from './buyerOnlyProfile.js';

export const BUYER_MAP_PATH = '/buyer-map';
// Signing in lands on the home page: it lists every tool the person
// can open and carries the "What's new" rail, which is the only place
// a shipped feature gets explained. The Deal Tracker is one click from
// there, and is still where a buyer-only profile never goes.
export const DEFAULT_APP_PATH = '/home';

// Incident kill switch for the public buyer map. /buyer-map renders
// with no app auth and its API routes answer anyone holding a share
// link, so a link believed to be compromised is closed by taking the
// whole surface down rather than revoking links one at a time.
//
// Fails closed: the map stays off unless VITE_BUYER_MAP_ENABLED is
// explicitly truthy. Keep this in step with BUYER_MAP_ENABLED on the
// API side (api/_lib/buyer-map-kill-switch.js) — the server is the
// real gate, this one only spares buyers a broken map.
const BUYER_MAP_ENABLED_VALUES = new Set(['1', 'true', 'yes', 'on', 'enabled']);

export function isBuyerMapEnabled() {
  const raw = String(import.meta.env?.VITE_BUYER_MAP_ENABLED ?? '').trim().toLowerCase();
  return BUYER_MAP_ENABLED_VALUES.has(raw);
}

function normalizeToken(value) {
  return normalizeAccessToken(value);
}

// The rule itself lives in buyerOnlyProfile.js, shared verbatim with the
// serverless routes — a buyer the browser hides the Lead Center from must
// be the same buyer the API refuses it to.
export function isBuyerOnlyUser(user) {
  return isBuyerOnlyProfile(user);
}

/**
 * Somebody who is not on this side of the table.
 *
 * A buyer-only login is the only kind today, and this stays a CONCEPT
 * rather than collapsing back into isBuyerOnlyUser because of what it
 * gates: the presence stack says which staff are online and what deal
 * they are reading, "Improve this page" is an internal change request,
 * Settings is the admin console. Each of those was once gated on
 * `buyerOnly` alone, and when a second kind of outsider briefly existed
 * every one of them had to be found and changed — the first such login
 * was shown a Settings link into the admin console.
 *
 * Gating on the concept means the next kind inherits it by existing.
 */
export function isOutsiderUser(user) {
  return isBuyerOnlyUser(user);
}

export function defaultSignedInPath(user) {
  if (isBuyerOnlyUser(user)) return BUYER_MAP_PATH;
  return DEFAULT_APP_PATH;
}

/**
 * The Lead Center — the CRM call list — is open to every agent.
 *
 * It used to be an allowlist of one or two named accounts, from when
 * the calling process was still being worked out on one desk. Calling
 * leads is the job now, so anyone on staff gets the list. The old
 * VITE_PRIVATE_CRM_EMAILS allowlist is gone rather than left dormant:
 * setting it again would quietly close the page for the whole team.
 *
 * Buyer-only logins are still refused. The roster is owner names, phone
 * numbers, and what we are willing to pay; it does not go to the other
 * side of the table. This matches the server, which answers the
 * /api/crm routes for any invited staff profile and nobody else.
 */
export function canAccessPrivateCrm(user) {
  if (!user) return false;
  return !isBuyerOnlyUser(user);
}

/**
 * The Buyers roster — who we sell to, what they pay, and how to reach
 * them — is admin-only.
 *
 * Agents work the buy side: owners, offers, deals. The buyer list is a
 * different business, and putting it in front of everyone invited the
 * team to work it instead of the deals in front of them.
 *
 * This hides the page and its nav. Buyer names still show on the deal
 * cards of deals an agent is actually working, which is the part they
 * need to do their job.
 */
export function canAccessBuyers(user) {
  return normalizeToken(user?.role) === 'admin';
}

/**
 * The investor roster — admin only, and for a harder reason than buyers.
 *
 * These rows carry each investor's rep commission rate and the
 * Paraclete / investor ownership split of the entity that takes title.
 * That is deal economics, not working information: a landman needs to
 * know a deal is spoken for, not what the investor's terms are.
 *
 * The same rule is the database's, not only this screen's — the RLS
 * policy on investor_profiles and investor_entities is is_admin(). This
 * gate exists so a non-admin gets a redirect instead of a page that
 * loads and then renders empty because every row was filtered away.
 */
export function canAccessInvestors(user) {
  return normalizeToken(user?.role) === 'admin';
}
